import { useCallback, useContext, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import { Delete as DeleteIcon, MoreVert as MoreVertIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { ColDef, ICellEditorParams, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { DATE_FORMATS, HistoricVolumeByWorkingUnitUploadResult } from '@callmart/types';
import CRUDSettings from '../components/General/CRUDSettings';
import {
    useChannels,
    useCreateWorkingUnit,
    useTasks,
    useUpdateWorkingUnit,
    useWorkingUnits,
    useWorktypes,
} from '../hooks';
import { getCellValue } from '../utils/agGridUtils';
import WorkingUnitDialog from '../components/WorkingUnits/WorkingUnitDialog';
import { WorkingUnitForm } from '../types/WorkingUnit.interface';
import { useExpireLocalStorage } from '../hooks/useExpireLocalStorage';
import UserContext from '../store/User/UserContext';
import { useDeleteHistoricVolumesByWorkingUnit, useUploadHistoricVolumeByWorkingUnit } from '../hooks/api/forecasts';
import DropzoneDialog from '../components/Dropzone/DropzoneDialog';
import ConfirmationDialog from '../components/UI/Dialogs/ConfirmationDialog';
import HistoricVolumeUploadResultDialog from '../components/Forecasts/HistoricVolumeUploadResultDialog';
import { appTimezone } from '../config';
import dayjs from '@utils/dayjs';

const WorkingUnits = () => {
    const loggedInUser = useContext(UserContext);
    const isForecastUser = loggedInUser?.isForecastUser ?? false;

    const [worktypes] = useWorktypes();
    const [channels] = useChannels();
    const [tasks] = useTasks();
    const [selectedWorkingUnit, setSelectedWorkingUnit] = useState(null);
    const [hasMatchingOfferedCluster, setHasMatchingOfferedCluster] = useExpireLocalStorage(
        'working-units-selected-matching',
        true
    );

    const [workingUnits, isWorkingUnitsLoading] = useWorkingUnits({
        hasMatchingOfferedCluster,
        location: undefined,
        withHistoricVolume: isForecastUser,
    });

    const [showCreateWorkingUnitDialog, setShowCreateWorkingUnitDialog] = useState(false);
    const { mutate: handleCreateWorkingUnit, isLoading: isWorkingUnitCreating } = useCreateWorkingUnit({
        onSettled: (data, error: any) => {
            if (!error) setShowCreateWorkingUnitDialog(false);
        },
    });
    const { mutate: handleUpdateWorkingUnit, isLoading: isWorkingUnitUpdating } = useUpdateWorkingUnit({
        onSettled: (data: any, error: any) => {
            if (!error) {
                setShowCreateWorkingUnitDialog(false);
                setSelectedWorkingUnit(null);
            }
        },
    });

    // Historic volume hooks
    const { mutateAsync: uploadByWorkingUnit, isLoading: isUploading } = useUploadHistoricVolumeByWorkingUnit();
    const { mutateAsync: deleteHVByWorkingUnit, isLoading: isDeleting } = useDeleteHistoricVolumesByWorkingUnit();

    // Historic volume state
    const [menuAnchor, setMenuAnchor] = useState<{
        element: HTMLElement;
        workingUnitId: number;
    } | null>(null);
    const [uploadTarget, setUploadTarget] = useState<{ workingUnitId: number; name: string } | null>(null);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [deleteTarget, setDeleteTarget] = useState<{ workingUnitId: number } | null>(null);
    const [uploadResult, setUploadResult] = useState<HistoricVolumeByWorkingUnitUploadResult | null>(null);

    // Historic volume handlers
    const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, workingUnitId: number) => {
        setMenuAnchor({ element: event.currentTarget, workingUnitId });
    }, []);

    const handleMenuClose = useCallback(() => {
        setMenuAnchor(null);
    }, []);

    const handleUploadClick = useCallback((workingUnitId: number, name: string) => {
        setUploadTarget({ workingUnitId, name });
    }, []);

    const handleUploadFromMenu = useCallback(() => {
        if (menuAnchor) {
            setUploadTarget({ workingUnitId: menuAnchor.workingUnitId, name: '' });
        }
        handleMenuClose();
    }, [menuAnchor, handleMenuClose]);

    const handleUploadClose = useCallback(() => {
        setUploadTarget(null);
        setUploadedFiles([]);
    }, []);

    const handleUploadSubmit = useCallback(
        async (files: File[]) => {
            if (files.length === 0 || !uploadTarget) return;
            try {
                const result = await uploadByWorkingUnit({ file: files[0], workingUnitId: uploadTarget.workingUnitId });
                setUploadResult(result);
                setUploadedFiles([]);
            } catch {
                // Error handled by hook
            }
        },
        [uploadTarget, uploadByWorkingUnit]
    );

    const handleDeleteFromMenu = useCallback(() => {
        if (menuAnchor) {
            setDeleteTarget({ workingUnitId: menuAnchor.workingUnitId });
        }
        handleMenuClose();
    }, [menuAnchor, handleMenuClose]);

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteTarget) return;
        try {
            await deleteHVByWorkingUnit(deleteTarget.workingUnitId);
            setDeleteTarget(null);
        } catch {
            // Error handled by hook
        }
    }, [deleteTarget, deleteHVByWorkingUnit]);

    const handleDeleteClose = useCallback(() => {
        setDeleteTarget(null);
    }, []);

    const handleUploadResultClose = useCallback(() => {
        setUploadResult(null);
        setUploadTarget(null);
        setUploadedFiles([]);
    }, []);

    const handleUploadAnother = useCallback(() => {
        setUploadResult(null);
        setUploadedFiles([]);
    }, []);

    const handleFormSubmit = useCallback(
        ({ id, worktype, channel, task, shortName, matchWithOfferedCluster }: WorkingUnitForm) => {
            const workingUnit = {
                ...(id && { id }),
                worktype,
                channel,
                task,
                shortName: shortName || null,
                matchWithOfferedCluster,
            };

            if (id) {
                // @ts-ignore
                handleUpdateWorkingUnit(workingUnit);
                return;
            }

            // @ts-ignore
            handleCreateWorkingUnit(workingUnit);
        },
        [handleCreateWorkingUnit, handleUpdateWorkingUnit]
    );

    const openCreateDialog = (value: any, isEdit?: boolean) => {
        if (!isEdit) {
            setSelectedWorkingUnit(null);
        }
        setShowCreateWorkingUnitDialog(value);
    };

    const getCommonColDefs = (
        values: Array<any> | ((arg0: any) => any),
        colName: string,
        secondColName?: string
    ): ColDef => {
        return {
            editable: (params) => params.data.isRowEdited || params.data.isNewRecord,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: (params: ICellEditorParams) => ({
                values: Array.isArray(values) ? values : values(params),
                cellRenderer: (params: ICellRendererParams) => getCellValue(params, colName, secondColName),
            }),
            cellRenderer: (params: ICellRendererParams) => getCellValue(params, colName, secondColName),
        };
    };

    const columnDefs = useMemo<Array<ColDef>>(() => {
        const cols: ColDef[] = [
            {
                field: 'worktype',
                headerName: 'Worktype',
                ...getCommonColDefs(worktypes, 'worktype'),
                filterParams: {
                    valueGetter: ({ data }: ValueGetterParams) => {
                        return data?.worktype?.worktype;
                    },
                },
                valueFormatter: ({ value }) => value?.worktype,
            },
            {
                field: 'channel',
                headerName: 'Kanal',
                ...getCommonColDefs(channels, 'name'),
                filterParams: {
                    valueGetter: ({ data }: ValueGetterParams) => {
                        return data?.channel?.name;
                    },
                },
                valueFormatter: ({ value }) => value?.name,
            },
            {
                field: 'task',
                headerName: 'Task',
                ...getCommonColDefs(tasks, 'name'),
                filterParams: {
                    valueGetter: ({ data }: ValueGetterParams) => {
                        return data?.task?.name;
                    },
                },
                valueFormatter: ({ value }) => value?.name,
            },
            {
                field: 'shortName',
                headerName: 'Short Name',
                valueGetter: ({ data }) => {
                    if (data.shortName) return data.shortName;
                    // Compose from components
                    const parts = [
                        data.worktype?.shortName || data.worktype?.worktype,
                        data.channel?.shortName || data.channel?.name,
                        data.task?.shortName || data.task?.name,
                    ].filter(Boolean);
                    return parts.join(' - ');
                },
            },
            {
                field: 'offeredClusterId',
                headerName: 'Offered Cluster',
                valueGetter: ({ data }) => (data.offeredClusterId ? 'Yes' : 'No'),
            },
            {
                field: 'budgetSiSClassification',
                headerName: 'Budget Classification',
                hide: true,
            },
        ];

        if (isForecastUser) {
            cols.push({
                headerName: 'Historic Volume',
                minWidth: 250,
                editable: false,
                filter: false,
                valueGetter: ({ data }) => {
                    if (!data) return -1;
                    return data.historicVolumeStats?.recordsCount ?? -1;
                },
                comparator: (a: number, b: number) => a - b,
                cellRenderer: (params: ICellRendererParams) => {
                    if (!params.data) return '';
                    const stats = params.data.historicVolumeStats;
                    if (!stats) {
                        return (
                            <Button
                                size="small"
                                startIcon={<UploadFileIcon />}
                                onClick={() => handleUploadClick(params.data.id, params.data.shortName || '')}
                            >
                                Upload
                            </Button>
                        );
                    }
                    const label = `${stats.recordsCount} records (${dayjs
                        .utc(stats.startDate)
                        .tz(appTimezone)
                        .format(DATE_FORMATS.MONTH_SHORT_YEAR)} - ${dayjs
                        .utc(stats.endDate)
                        .tz(appTimezone)
                        .format(DATE_FORMATS.MONTH_SHORT_YEAR)})`;
                    return (
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                {label}
                            </Typography>
                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.data.id)}>
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    );
                },
            });
        }

        return cols;
    }, [worktypes, channels, tasks, isForecastUser, handleUploadClick, handleMenuOpen]);

    const filters = () => (
        <FormControlLabel
            control={
                <Checkbox
                    name="match"
                    checked={hasMatchingOfferedCluster}
                    onChange={(e) => setHasMatchingOfferedCluster(e.target.checked)}
                />
            }
            label="Match with Offered Cluster"
        />
    );

    return (
        <>
            <CRUDSettings
                tableRows={workingUnits}
                columnDefs={columnDefs}
                btnText="Neuer Working Unit"
                recordType="Working Unit"
                pagination={true}
                paginationPageSize={20}
                paginationPageSizeSelector={[10, 20, 50, 100]}
                isCreationViaDialog={true}
                filters={filters}
                isEditViaDialog={true}
                setShowCreationDialog={openCreateDialog}
                onSelectedRowChange={(selectedRow: any) => setSelectedWorkingUnit(selectedRow)}
                loading={isWorkingUnitsLoading || isWorkingUnitCreating || isWorkingUnitUpdating}
            />
            <WorkingUnitDialog
                workingUnit={selectedWorkingUnit}
                taskOptions={tasks}
                channelOptions={channels}
                worktypeOptions={worktypes}
                showDialog={showCreateWorkingUnitDialog}
                onClose={() => setShowCreateWorkingUnitDialog(false)}
                onSubmit={handleFormSubmit}
                isLoading={isWorkingUnitCreating}
            />
            {isForecastUser && (
                <>
                    <Menu
                        anchorEl={menuAnchor?.element}
                        open={Boolean(menuAnchor)}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <MenuItem onClick={handleUploadFromMenu}>
                            <ListItemIcon>
                                <UploadFileIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Upload new data</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={handleDeleteFromMenu} sx={{ color: 'error.main' }}>
                            <ListItemIcon>
                                <DeleteIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            <ListItemText>Delete data</ListItemText>
                        </MenuItem>
                    </Menu>
                    <DropzoneDialog
                        open={!!uploadTarget && !uploadResult}
                        onClose={handleUploadClose}
                        onUpload={handleUploadSubmit}
                        dialogTitle={`Upload Historic Volume${uploadTarget?.name ? ` - ${uploadTarget.name}` : ''}`}
                        multiple={false}
                        accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                        uploadedFiles={uploadedFiles}
                        onUploadedFilesChange={setUploadedFiles}
                        loading={isUploading}
                    />
                    {uploadResult && (
                        <HistoricVolumeUploadResultDialog
                            open={!!uploadResult}
                            result={uploadResult}
                            onClose={handleUploadResultClose}
                            onUploadAnother={handleUploadAnother}
                        />
                    )}
                    {deleteTarget && (
                        <ConfirmationDialog
                            open={!!deleteTarget}
                            title="Delete Historic Volume Data"
                            text={`Are you sure you want to delete all historic volume data for this working unit? This action is irreversible.`}
                            onClose={handleDeleteClose}
                            onConfirm={handleDeleteConfirm}
                            isLoading={isDeleting}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default WorkingUnits;
